import { createSelector, Selector, MemoizedSelector, DefaultProjectorFn } from '@ngrx/store';
import {Observable, of, combineLatestWith} from 'rxjs';
import { takeUntil, repeat, switchMap } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { whenFalse, whenTrue } from './rx.util';

/*
    AsyncActionState credit github.com/j-mcneil/ngrx-workshop
*/
export interface AsyncActionState {
    error: any;
    isLoading: boolean;
    success: boolean;
}

export interface AsyncActionSelectors {
    getError: MemoizedSelector<object, any, DefaultProjectorFn<any>>;
    getHasError: MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    getIsLoading: MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    getNeedsLoading: MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
    getSuccess: MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>>;
}

export interface AsyncActionQueries {
    error$: Observable<any>;
    hasError$: Observable<boolean>;
    isLoading$: Observable<boolean>;
    needsLoading$: Observable<boolean>;
    success$: Observable<boolean>;
}

export const getIsLoading = (state: AsyncActionState) => state?.isLoading;
export const getSuccess = (state: AsyncActionState) => state.success;
export const getError = (state: AsyncActionState) => state.error;

export function getSelectors(
    selectState: Selector<object, AsyncActionState>
): AsyncActionSelectors {
    const getIsLoadingSelector = createSelector(selectState, getIsLoading);
    const getSuccessSelector = createSelector(selectState, getSuccess);
    const getErrorSelector = createSelector(selectState, getError);

    return {
        getError: getErrorSelector,
        getHasError: createSelector(getErrorSelector, RA.isTruthy),
        getIsLoading: getIsLoadingSelector,
        getNeedsLoading: createSelector(
            getIsLoadingSelector,
            getErrorSelector,
            getSuccessSelector,
            R.complement(R.unapply(R.any(RA.isTruthy)))
        ),
        getSuccess: getSuccessSelector
    };
}

export const initialState: AsyncActionState = {
    error: null,
    isLoading: false,
    success: false
};

export const loadingState: AsyncActionState = {
    error: null,
    isLoading: true,
    success: false
};

export const successState: AsyncActionState = {
    error: null,
    isLoading: false,
    success: true
};

export const errorState = (error: any): AsyncActionState => ({
    error,
    isLoading: false,
    success: false
});

export function getAsyncActionValue<T>(
    source$: Observable<T>,
    asyncActionQueries: AsyncActionQueries
) {
    const { success$, needsLoading$, hasError$ } = asyncActionQueries;
    const validState$ = of(whenTrue(success$)).pipe(combineLatestWith(whenFalse(needsLoading$)));

    return validState$.pipe(switchMap(() => source$.pipe(takeUntil(whenTrue(hasError$)))), repeat());
}

export const asyncActionState = {
    error: errorState,
    getSelectors,
    initial: initialState,
    loading: loadingState,
    success: successState
};
